import * as React from 'react';
import { Dialog, DialogFooter, Label, Link } from 'office-ui-fabric-react';
import { AppButton, getAppButtonStyles, getSecondaryButtonOverrideStyles } from '../AppButton';
import { getTopLabelStyles, getModalProps, getDialogContentProps, getDialogStyles } from '../../common/styles/Popup.styles';
import { AppLayoutSettings } from '../../common/styles/common.styles';
import dispatcher from '../../dispatcher';
import { ChangeEvent } from '../../stores/utils';
import { EventEmitter } from 'events';
import { EMPTY_STRING } from '../../../AppConstants';
import { isEmptyString, isNullOrUndefined } from '../../../utils/utils';

export enum TranscriptionAlertButtonType {
  PRIMARY_BUTTON = 1,
  SECONDARY_BUTTON = 2,
  LINK = 4,
  NONE = 8
};

/**
 * TranscriptionAlert can have below priorities, which can be passed as Priority of TranscriptionAlertState.
 * NORMAL Alert will be shown in order.
 * CRITICAL Alert will be immidiately shown to user and existing request will be shown after this.
 */
export enum TranscriptionAlertPriority {
  NORMAL = 1, //Alert will be shown in order
  CRITICAL = 2, // Alert will be immidiately shown to user and existing request will be shown after this.
};

export interface TranscriptionAlertState {
  showTranscriptionAlert?: boolean,
  information: string,
  title: string,
  buttonInfo: TranscriptionAlertButtonState,
  id?: string, // Id is required in case dialog need to be removed, this id need to be passed as payload with event TranscriptionAlertActions.RemoveTranscriptionAlert
  priority?: TranscriptionAlertPriority
};

export interface TranscriptionAlertButtonState {
  buttonType: TranscriptionAlertButtonType,
  primaryButtonText?: string,
  secondaryButtonText?: string,
  buttonClickListener?: TranscriptionAlertButtonClickListener | null,
  hyperLink?: string,
  linkText?: string
}

export interface TranscriptionAlertButtonClickListener {
  onButtonClicked(buttonType: TranscriptionAlertButtonType): void;
}

export const TranscriptionAlertActions = {
  ShowTranscriptionAlert: 'SHOW_TRANSCRIPTION_ALERT',
  RemoveTranscriptionAlert: 'REMOVE_TRANSCRIPTION_ALERT'
};

/* TranscriptionAlert is a generic Dialog, which can be used to display information text
and title at runtime, the caller need to pass in all requirement in form of {TranscriptionAlertState},
{TranscriptionAlertButtonState}.
Also caller can register callback for Dialog <buttons></buttons>

sample code {
const listener: TranscriptionAlertButtonClickListener = <TranscriptionAlertButtonClickListener>({
onButtonClicked(buttonType: TranscriptionAlertButtonType): void {}
});

const TranscriptionAlertState: TranscriptionAlertState = {
    information: "",
    title: "",
    buttonInfo: { buttonType: TranscriptionAlertButtonType.POSITIVE_BUTTON, buttonClickListener: listener }
}

dispatcher.dispatch({
    type: TranscriptionAlertActions.ShowTranscriptionAlert,
    payload: TranscriptionAlertState
});
}*/

export class TranscriptionAlert extends React.PureComponent<{}, TranscriptionAlertState> {

  constructor(props: {}) {
    super(props);
    this.state = transcriptionAlertManager.getState();
  }

  public componentDidMount() {
    transcriptionAlertManager.on(ChangeEvent.Change, this._handleUpdate);
  }

  public componentWillUnmount() {
    transcriptionAlertManager.off(ChangeEvent.Change, this._handleUpdate);
  }

  public render() {
    if (!this.state.showTranscriptionAlert) {
      return null;
    }
    const confirmNewTranscriptDialogTitle: string | JSX.Element =
      <Label styles={ getTopLabelStyles() }>{ this.state.title }</Label>

    return (
      <Dialog
        dialogContentProps={ getDialogContentProps(confirmNewTranscriptDialogTitle) }
        modalProps={ getModalProps() }
        hidden={ !this.state.showTranscriptionAlert }
        styles={ getDialogStyles('10px') }>
        { this.state.information.length > 0 ?
          <Label styles={ AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles() }>{ this.state.information }</Label> : null }
        { this.showButton() }
      </Dialog>
    );
  }

  private _handleUpdate = () => {
    this.setState(transcriptionAlertManager.getState());
  }

  private showButton = () => {
    if (this.state.buttonInfo.buttonType & TranscriptionAlertButtonType.NONE) {
      return null;
    }
    return (<DialogFooter>
      { this.state.buttonInfo.buttonType & TranscriptionAlertButtonType.LINK ?
        <Link
          id="TranscriptionLink"
          styles={ AppLayoutSettings.AppCommonStyles.getLinkStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx, AppLayoutSettings.getAppColorContainer().linkColor) }
          href={ this.state.buttonInfo.hyperLink } target="_blank"
          onClick={ this._onLinkClicked }>
          { this.state.buttonInfo.linkText }
        </Link> : null }
      { this.state.buttonInfo.buttonType & TranscriptionAlertButtonType.PRIMARY_BUTTON ?
        <AppButton
          type={ 'AppButtonProps' }
          id="TranscriptionPrimaryButton"
          styles={ getAppButtonStyles() }
          text={ this.state.buttonInfo.primaryButtonText == undefined ? TranscriptionStrings.OKLabel : this.state.buttonInfo.primaryButtonText }
          onExecute={ this._onPrimaryButtonClicked }
        /> : null }
      { this.state.buttonInfo.buttonType & TranscriptionAlertButtonType.SECONDARY_BUTTON ?
        <AppButton
          type={ 'AppButtonProps' }
          id="TranscriptionSecondaryButton"
          styles={ getSecondaryButtonOverrideStyles() }
          text={ this.state.buttonInfo.secondaryButtonText == undefined ? TranscriptionStrings.CancelButtonName : this.state.buttonInfo.secondaryButtonText }
          onExecute={ this._onSecondaryButtonClicked }
        /> : null }
    </DialogFooter>);
  }

  private _onPrimaryButtonClicked = () => {
    transcriptionAlertManager.onButtonClicked(TranscriptionAlertButtonType.PRIMARY_BUTTON);
  }

  private _onLinkClicked = () => {
    if (!isNullOrUndefined(this.state.buttonInfo.buttonClickListener)) {
      this.state.buttonInfo.buttonClickListener?.onButtonClicked(TranscriptionAlertButtonType.LINK);
    }
  }

  private _onSecondaryButtonClicked = () => {
    transcriptionAlertManager.onButtonClicked(TranscriptionAlertButtonType.SECONDARY_BUTTON);
  }
}

class TranscriptionAlertManager extends EventEmitter {

  private state: TranscriptionAlertState = {
    showTranscriptionAlert: false,
    information: EMPTY_STRING,
    title: EMPTY_STRING,
    buttonInfo: { buttonType: TranscriptionAlertButtonType.PRIMARY_BUTTON },
    id: EMPTY_STRING,
    priority: TranscriptionAlertPriority.NORMAL
  };

  private transcriptionAlertQueueNormal: Array<any> = new Array<any>();
  private transcriptionAlertQueueCritical: Array<any> = new Array<any>();

  private transcriptionEventMap = new Map();

  constructor() {
    super();
    dispatcher.register(this.handleUpdate);
  }

  public getState = (): TranscriptionAlertState => {
    return Object.assign({}, this.state);
  }

  public handleUpdate = (newAlertRequest: any): void => {
    switch (newAlertRequest.type) {
      case TranscriptionAlertActions.ShowTranscriptionAlert:

        const newTranscriptionAlertState: TranscriptionAlertState = ((newAlertRequest).payload);
        // If Information pop up is already showing.
        if (this.state.showTranscriptionAlert) {

          /* If shown alert is CRITICAL and new Alert request is NORMAL, New Alert request should be wrapped up and moved to
             transcriptionEventQueue */
          if (this.state.priority == TranscriptionAlertPriority.CRITICAL && newTranscriptionAlertState.priority != TranscriptionAlertPriority.CRITICAL) {
            this.addRequestToTranscriptionEventQueue(newAlertRequest);
            return;
          }

          /* in all other cases, New Alert request should be shown immediately and existing alert, which is being shown should be wrapped up and moved to top of
          transcriptionEventQueue */
          const existingTranscriptionAlertState: TranscriptionAlertState = {
            information: this.state.information,
            title: this.state.title,
            buttonInfo: this.state.buttonInfo,
            id: this.state.id,
            priority: this.state.priority,
            showTranscriptionAlert: this.state.showTranscriptionAlert
          }
          const existingAlert = {
            type: TranscriptionAlertActions.ShowTranscriptionAlert,
            payload: existingTranscriptionAlertState
          };

          this.addRequestToTranscriptionEventQueue(existingAlert);
        }

        //show new Transcription Alert
        this.state.information = newTranscriptionAlertState.information;
        this.state.title = newTranscriptionAlertState.title;
        this.state.showTranscriptionAlert = true;
        this.state.id = newTranscriptionAlertState.id;
        this.state.buttonInfo = newTranscriptionAlertState.buttonInfo;
        this.state.priority = !isNullOrUndefined(newTranscriptionAlertState.priority) ? newTranscriptionAlertState.priority : this.state.priority;
        this.emit(ChangeEvent.Change);
        break;
      case TranscriptionAlertActions.RemoveTranscriptionAlert:
        const transcriptionAlertId: string = ((newAlertRequest).payload);
        if (this.state.showTranscriptionAlert &&
          this.state.id == transcriptionAlertId) {
          this.state.showTranscriptionAlert = false;
          this.state.buttonInfo.buttonClickListener = null;
          this.emit(ChangeEvent.Change);
          this.fetchAndShowNextAlertFromQueue();
        } else {
          this.removeRequestFromTranscriptionEventQueue(transcriptionAlertId);
        }
        break;
    }
  }

  private addRequestToTranscriptionEventQueue(alertRequest: any) {
    const transcriptionAlertState: TranscriptionAlertState = ((alertRequest).payload);
    if (transcriptionAlertState.priority == TranscriptionAlertPriority.CRITICAL) {
      this.transcriptionAlertQueueCritical.push(alertRequest);
    } else {
      this.transcriptionAlertQueueNormal.push(alertRequest);
    }
    if (!isEmptyString(transcriptionAlertState.id!)) {
      this.transcriptionEventMap.set(transcriptionAlertState.id, alertRequest);
    }
  }

  private removeRequestFromTranscriptionEventQueue(transcriptionAlertId: string) {
    let alertRequest = this.transcriptionEventMap.get(transcriptionAlertId);
    if (!isNullOrUndefined(alertRequest)) {
      alertRequest.type = TranscriptionAlertActions.RemoveTranscriptionAlert;
      this.transcriptionEventMap.delete(transcriptionAlertId);
    }
  }

  onButtonClicked = (type: TranscriptionAlertButtonType) => {
    this.state.showTranscriptionAlert = false;
    this.emit(ChangeEvent.Change);

    if (!isNullOrUndefined(this.state.buttonInfo.buttonClickListener)) {
      this.state.buttonInfo.buttonClickListener?.onButtonClicked(type);
      this.state.buttonInfo.buttonClickListener = null;
    }
    this.fetchAndShowNextAlertFromQueue();
  }

  private fetchAndShowNextAlertFromQueue(): boolean {

    // first priority is for critical transcriptionEventQueue.
    let isSuccess: boolean = this.ShowNextAlertFromQueueWithPriority(TranscriptionAlertPriority.CRITICAL);
    if (!isSuccess) {

      // if critical transcriptionEventQueue is empty, check for normal transcriptionEventQueue queue
      isSuccess = this.ShowNextAlertFromQueueWithPriority(TranscriptionAlertPriority.NORMAL);
    }
    return isSuccess;
  }

  private ShowNextAlertFromQueueWithPriority(priority: number): boolean {
    const transcriptionAlertqueue = priority == TranscriptionAlertPriority.CRITICAL ? this.transcriptionAlertQueueCritical : this.transcriptionAlertQueueNormal;
    while (transcriptionAlertqueue.length > 0) {
      const alertRequest: any = transcriptionAlertqueue.pop();
      if (alertRequest != undefined && alertRequest.type == TranscriptionAlertActions.ShowTranscriptionAlert) {
        const newTranscriptionAlertState: TranscriptionAlertState = ((alertRequest).payload);
        this.transcriptionEventMap.delete(newTranscriptionAlertState);
        this.handleUpdate(alertRequest);
        return true; //new alert request has been sent to TranscriptionAlert UX handler.
      }
    }
    return false;
  }
}

const transcriptionAlertManager = new TranscriptionAlertManager();
export default transcriptionAlertManager;